import { ContentCopy, Delete, MoreVert } from '@mui/icons-material'
import { IconButton, ListItemIcon, ListItemText, Menu, MenuItem, Typography } from '@mui/material'
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'
import React, { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { Project, User, WorkforceSchedule } from '../../../store/types'
import DuplicateForMonthPopup from './DuplicateForMonthPopup'
import {
  WorkforceScheduleModify,
  useCreateWorkforceScheduleMutation,
} from '../../../store/redux/reducers/workforceScheduleSlice'
import { useAppSelector, useNotification } from '../../../store/redux/hooks'
import DateFormat from '../../../store/types/enums/DateFormat'
import dayjs, { Dayjs } from 'dayjs'
import { usePopupState } from 'material-ui-popup-state/hooks'
import Demand from '../../../store/types/models/Demand'
import { useEditDemandMutation } from '../../../store/redux/reducers/demandSlice'
import { createErrorMessage } from '../../../store/utils'

const MonthMenuPopup = ({
  data,
  demandData,
  setEmployees,
  setProjects,
  month,
}: {
  data: WorkforceSchedule[]
  demandData: Demand[]
  setEmployees: React.Dispatch<React.SetStateAction<User[]>>
  setProjects: React.Dispatch<React.SetStateAction<Project[]>>
  month: Dayjs
}) => {
  const { t } = useTranslation()
  const [createWorkforce] = useCreateWorkforceScheduleMutation()
  const { displayNotification } = useNotification()
  const { leaderId } = useAppSelector(state => state.leaderId)
  const targetRef = useRef(null)
  const popupStatePopover = usePopupState({ variant: 'popover', popupId: 'duplicate-for-month' })
  const [editDemand] = useEditDemandMutation()

  const handleMonthDataRemoveClick = async () => {
    if (!data.length) {
      return
    }

    setEmployees([])
    setProjects([])

    for (const demand of demandData) {
      try {
        await editDemand({
          id: demand.id,
          demand: {
            numberOfPeople: demand.numberOfPeople,
            technologies: demand.technologies,
            projectId: demand.project.id,
            submitterId: demand.submitter.id,
            startDate: dayjs(demand.startDate),
            state: demand.state,
            description: demand.description,
            completed: true,
            version: demand.version,
          },
        }).unwrap()
      } catch (error) {
        let errorMessage = createErrorMessage(error)
        displayNotification({ message: `${t('page.demands.alert.demandDeleteError')} ${errorMessage}`, type: 'error' })
        return
      }
    }

    const removeWorkforceData: WorkforceScheduleModify[] = data.map(data => {
      const removeData: WorkforceScheduleModify = {
        ...data,
        projectId: data.project.id,
        employeeId: data.employee.id,
        date: dayjs(data.date).format(DateFormat.YearMonth),
        removed: true,
      }

      return removeData
    })

    try {
      await createWorkforce({ leaderId: leaderId!!, workforceSchedules: removeWorkforceData }).unwrap()
      displayNotification({
        message: t('page.projectDistribution.removedSuccessfully'),
        type: 'success',
      })
    } catch (error) {
      let errorMessage = createErrorMessage(error)
      displayNotification({ message: `${t('message.somethingWentWrong')} ${errorMessage}`, type: 'error' })
    }
  }

  return (
    <PopupState variant="popover" popupId="month-menu-popper" disableAutoFocus={false} parentPopupState={null}>
      {popupState => (
        <React.Fragment>
          <IconButton
            aria-label={t('page.projectDistribution.aria.monthMenuButton')}
            {...bindTrigger(popupState)}
            sx={{ svg: { color: popupState.isOpen ? 'text.primary' : 'text.mediumEmphasis' },
              backgroundColor: popupState.isOpen ? 'action.hover' : 'transparent',
              transition: 'background-color 0.2s ease-in-out',
            }}
          >
            <MoreVert style={{ width: 16, height: 16 }} />
          </IconButton>
          <Menu
            {...bindMenu(popupState)}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            slotProps={{ paper: { elevation: 3 } }}
            sx={{ color: 'text.base' }}
          >
            <MenuItem {...bindTrigger(popupStatePopover)}>
              <ListItemIcon sx={{ svg: { color: 'text.mediumEmphasis' } }}>
                <ContentCopy />
              </ListItemIcon>
              <ListItemText>
                <Typography ref={targetRef} color="text.base">
                  {t('page.projectDistribution.duplicate')}
                </Typography>
              </ListItemText>
            </MenuItem>

            <MenuItem
              onClick={() => {
                handleMonthDataRemoveClick()
                popupState.close()
              }}
              sx={{ svg: { color: 'text.mediumEmphasis' } }}
            >
              <ListItemIcon>
                <Delete />
              </ListItemIcon>
              <ListItemText sx={{ color: 'text.base' }}>{t('page.projectDistribution.clear')}</ListItemText>
            </MenuItem>
          </Menu>
          <DuplicateForMonthPopup
            monthToDuplicateData={data}
            targetRef={targetRef}
            popupState={popupStatePopover}
            demandToDuplicateData={demandData}
            month={month}
          />
        </React.Fragment>
      )}
    </PopupState>
  )
}

export default MonthMenuPopup